import { createContext, useContext, useEffect, useState } from 'react'
import { BrandKey, BrandTheme, BRAND_DEFAULTS, resolveBrandKey, applyBrandCssVars } from './brands'
import { apiClient } from '@/api/client'

interface BrandContextValue {
  brandKey: BrandKey
  theme: BrandTheme
  loading: boolean
}

const BrandContext = createContext<BrandContextValue>({
  brandKey: 'yansar',
  theme: BRAND_DEFAULTS.yansar,
  loading: true,
})

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brandKey] = useState<BrandKey>(resolveBrandKey)
  const [theme, setTheme] = useState<BrandTheme>(BRAND_DEFAULTS[brandKey])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Apply fallback immediately so UI doesn't flash unstyled
    applyBrandCssVars(BRAND_DEFAULTS[brandKey])

    // Fetch live brand config from API (allows CMS-driven brand updates)
    apiClient.get<Partial<BrandTheme>>(`/brand/${brandKey}`)
      .then(res => {
        // Merge over the defaults rather than replacing them: a 200 carrying a
        // partial body would otherwise leave the theme with undefined fields,
        // and the CSS vars derived from them unusable.
        const merged = { ...BRAND_DEFAULTS[brandKey], ...(res.data ?? {}) }
        setTheme(merged)
        applyBrandCssVars(merged)
      })
      .catch(() => {
        // API unavailable — fallback defaults already applied, keep them
      })
      .finally(() => setLoading(false))
  }, [brandKey])

  return (
    <BrandContext.Provider value={{ brandKey, theme, loading }}>
      {children}
    </BrandContext.Provider>
  )
}

export function useBrand() {
  return useContext(BrandContext)
}
